import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";

const LOGO = "img/logoEverRice.png";

type NavItem = { key: string; hash?: string; path?: string };

const items: NavItem[] = [
  { key: "home", hash: "home" },
  { key: "aboutUs", path: "/about-us" },
  { key: "products", path: "/products" },
  { key: "quality", path: "/quality" },
  { key: "sustainability", path: "/sustainability" },
  { key: "globalReach", path: "/global-reach" },
  { key: "contact", path: "/contact" },
];

export default function SiteNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const handleNav = (e: React.MouseEvent, item: NavItem) => {
    e.preventDefault();
    setOpen(false);
    if (item.path) {
      navigate(item.path);
      window.scrollTo({ top: 0 });
      return;
    }
    if (!item.hash) return;
    if (location.pathname === "/") scrollTo(item.hash);
    else {
      navigate("/");
      window.setTimeout(() => scrollTo(item.hash as string), 120);
    }
  };

  const isActive = (item: NavItem) =>
    item.path
      ? location.pathname === item.path
      : location.pathname === "/" && item.hash === "home";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors ${scrolled || open || location.pathname !== "/" ? "bg-background-50 border-b border-background-200" : "bg-transparent"}`}
    >
      <nav className="flex items-center justify-between page-gutter h-20 lg:h-24">
        <button
          type="button"
          onClick={(e) => handleNav(e, { key: "home", path: "/" })}
          className="flex items-center cursor-pointer"
        >
          <img
            src={LOGO}
            alt="EverRice logo"
            title="EverRice"
            className="h-[3rem] lg:h-[4rem] w-auto object-contain"
          />
        </button>
        <ul className="hidden lg:flex items-center gap-8">
          {items.map((l) => (
            <li key={l.key}>
              <a
                href={l.path ?? `/#${l.hash}`}
                onClick={(e) => handleNav(e, l)}
                className={`text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:text-primary-600 ${isActive(l) ? "text-foreground-950 border-b border-foreground-950 pb-1" : "text-foreground-700"}`}
              >
                {t(`nav.${l.key}`)}
              </a>
            </li>
          ))}
        </ul>
        <div className="hidden lg:flex items-center">
          <LanguageSwitcher />
        </div>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer whitespace-nowrap"
        >
          <i
            className={`${open ? "ri-close-line" : "ri-menu-line"} text-2xl`}
          />
        </button>
      </nav>
      {open && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-4">
          <ul className="flex flex-col gap-3">
            {items.map((l) => (
              <li key={l.key}>
                <a
                  href={l.path ?? `/#${l.hash}`}
                  onClick={(e) => handleNav(e, l)}
                  className="text-sm uppercase tracking-widest cursor-pointer"
                >
                  {t(`nav.${l.key}`)}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-background-200">
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </header>
  );
}
